import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useConstellation } from '../hooks/useConstellation';
import * as api from '../services/api';
import './Dashboard.css';

type ChartView = 'total_asset_value' | 'pnl' | 'percentage_total_asset_growth' | 'percentage_pnl_growth';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [selectedInvestor, setSelectedInvestor] = useState<string>(user?.investorId || '');
  const [investors, setInvestors] = useState<string[]>([]);
  const [monthlyReport, setMonthlyReport] = useState<api.MonthlyReport | null>(null);
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('lifetime');
  const [lineGraphData, setLineGraphData] = useState<any[]>([]);
  const [chartView, setChartView] = useState<ChartView>('percentage_pnl_growth');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  
  // Add constellation background effect
  useConstellation();

  useEffect(() => {
    if (user?.isAdmin) {
      loadInvestors();
    }
    if (selectedInvestor) {
      loadDashboardData();
    }
  }, [user, selectedInvestor]);

  useEffect(() => {
    if (selectedInvestor && selectedMonth) {
      loadMonthlyReport();
    }
  }, [selectedInvestor, selectedMonth]);

  const loadInvestors = async () => {
    try {
      const data = await api.getInvestorList();
      setInvestors(data.investors);
    } catch (err) {
      console.error('Failed to load investors:', err);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      const [graphData, months] = await Promise.all([
        api.getLineGraphData(selectedInvestor),
        api.getAvailableMonths(selectedInvestor)
      ]);

      // Transform graph data for Recharts
      // Convert log returns to simple returns: simple = e^(log) - 1
      const chartData = Object.entries(graphData).map(([date, data]) => ({
        date,
        ...data,
        percentage_pnl_growth: (Math.exp(data.percentage_pnl_growth / 100) - 1) * 100,
        percentage_total_asset_growth: (Math.exp(data.percentage_total_asset_growth / 100) - 1) * 100,
      }));

      setLineGraphData(chartData);
      setAvailableMonths(months.months);
    } catch (err: any) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyReport = async () => {
    try {
      const report = selectedMonth === 'lifetime'
        ? await api.getCurrentMonthReport(selectedInvestor)
        : await api.getMonthlyReport(selectedInvestor, selectedMonth);
      setMonthlyReport(report);
    } catch (err) {
      console.error('Failed to load monthly report:', err);
      setMonthlyReport(null);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const formatCrypto = (value: number, symbol: string) => {
    return `${value.toFixed(8)} ${symbol}`;
  };

  const hasBalance = (balance: number) => {
    return balance > 0;
  };

  const getChartConfig = () => {
    switch (chartView) {
      case 'total_asset_value':
        return {
          dataKey: 'total_asset_value',
          name: 'Total Asset Value',
          formatter: formatCurrency,
          color: '#FF5740' // flare
        };
      case 'pnl':
        return {
          dataKey: 'pnl',
          name: 'Cumulative P&L',
          formatter: formatCurrency,
          color: '#1B101F' // nightshade
        };
      case 'percentage_total_asset_growth':
        return {
          dataKey: 'percentage_total_asset_growth',
          name: 'Total Asset Growth %',
          formatter: formatPercentage,
          color: '#858170' // urbanite
        };
      case 'percentage_pnl_growth':
        return {
          dataKey: 'percentage_pnl_growth',
          name: 'P&L Growth %',
          formatter: formatPercentage,
          color: '#547AFF' // bluebolt
        };
      default:
        return {
          dataKey: 'percentage_pnl_growth',
          name: 'P&L Growth %',
          formatter: formatPercentage,
          color: '#547AFF' // bluebolt
        };
    }
  };

  const chartConfig = getChartConfig();

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="dashboard-error">{error}</div>;
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1 className="headline">Upstream Alpha Fund</h1>
        <div className="dashboard-controls">
          {user?.isAdmin && (
            <div className="meta-item">
              <strong>INVESTOR:</strong>
              <select
                value={selectedInvestor}
                onChange={(e) => setSelectedInvestor(e.target.value)}
                className="form-select"
                style={{marginLeft: '8px'}}
              >
                <option value="">Select Investor</option>
                {investors.map(inv => (
                  <option key={inv} value={inv}>{inv}</option>
                ))}
              </select>
            </div>
          )}
          
          <div className="meta-item">
            <strong>REPORT PERIOD:</strong>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="form-select"
              style={{marginLeft: '8px'}}
            >
              <option value="lifetime">Lifetime</option>
              {availableMonths.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <section className="section">
          <h2 className="section-title">Performance Overview</h2>
          <div className="chart-view-selector">
            <button
              className={`chart-view-btn ${chartView === 'percentage_pnl_growth' ? 'active' : ''}`}
              onClick={() => setChartView('percentage_pnl_growth')}
            >
              P&L Growth %
            </button>
            <button
              className={`chart-view-btn ${chartView === 'pnl' ? 'active' : ''}`}
              onClick={() => setChartView('pnl')}
            >
              P&L
            </button>
            <button
              className={`chart-view-btn ${chartView === 'percentage_total_asset_growth' ? 'active' : ''}`}
              onClick={() => setChartView('percentage_total_asset_growth')}
            >
              Asset Growth %
            </button>
            <button
              className={`chart-view-btn ${chartView === 'total_asset_value' ? 'active' : ''}`}
              onClick={() => setChartView('total_asset_value')}
            >
              Asset Value
            </button>
          </div>

          <div className="chart-container">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={lineGraphData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="date" 
                  stroke="var(--text-muted)"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  stroke="var(--text-muted)"
                  tick={{ fontSize: 12 }}
                  tickFormatter={chartConfig.formatter}
                />
                <Tooltip 
                  formatter={chartConfig.formatter}
                  contentStyle={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey={chartConfig.dataKey}
                  name={chartConfig.name}
                  stroke={chartConfig.color}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {monthlyReport && (
          <>
            <section className="section">
              <h2 className="section-title">Portfolio Summary</h2>
              <div className="portfolio-summary">
                <div className="balance-section">
                  <h3>Starting Balance</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Asset</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hasBalance(monthlyReport.starting_balance.USD_BALANCE) && (
                        <tr>
                          <td>USD Balance</td>
                          <td className="numerals">{formatCurrency(monthlyReport.starting_balance.USD_BALANCE)}</td>
                        </tr>
                      )}
                      {hasBalance(monthlyReport.starting_balance.BTC_BALANCE) && (
                        <tr>
                          <td>BTC Balance</td>
                          <td className="numerals">{formatCrypto(monthlyReport.starting_balance.BTC_BALANCE, 'BTC')}</td>
                        </tr>
                      )}
                      {hasBalance(monthlyReport.starting_balance.ETH_BALANCE) && (
                        <tr>
                          <td>ETH Balance</td>
                          <td className="numerals">{formatCrypto(monthlyReport.starting_balance.ETH_BALANCE, 'ETH')}</td>
                        </tr>
                      )}
                      <tr className="total-row">
                        <td>Total USD Value</td>
                        <td className="numerals highlight">{formatCurrency(monthlyReport.starting_balance.TOTAL_USD_BALANCE)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="balance-section">
                  <h3>{selectedMonth === 'lifetime' ? 'Current Balance' : 'Ending Balance'}</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Asset</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hasBalance(monthlyReport.ending_balance.USD_BALANCE) && (
                        <tr>
                          <td>USD Balance</td>
                          <td className="numerals">{formatCurrency(monthlyReport.ending_balance.USD_BALANCE)}</td>
                        </tr>
                      )}
                      {hasBalance(monthlyReport.ending_balance.BTC_BALANCE) && (
                        <tr>
                          <td>BTC Balance</td>
                          <td className="numerals">{formatCrypto(monthlyReport.ending_balance.BTC_BALANCE, 'BTC')}</td>
                        </tr>
                      )}
                      {hasBalance(monthlyReport.ending_balance.ETH_BALANCE) && (
                        <tr>
                          <td>ETH Balance</td>
                          <td className="numerals">{formatCrypto(monthlyReport.ending_balance.ETH_BALANCE, 'ETH')}</td>
                        </tr>
                      )}
                      <tr className="total-row">
                        <td>Total USD Value</td>
                        <td className="numerals highlight">{formatCurrency(monthlyReport.ending_balance.TOTAL_USD_BALANCE)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section className="section">
              <h2 className="section-title">Performance Overview</h2>
              <div className="metrics-grid">
                {(hasBalance(monthlyReport.ending_balance.BTC_BALANCE) || hasBalance(monthlyReport.ending_balance.ETH_BALANCE)) && (
                  <div className="metric-card">
                    <div className="metric-label">Total Return</div>
                    <div className="metric-value numerals positive">{formatPercentage(monthlyReport.performance_summary.total_return_percent)}</div>
                  </div>
                )}
                <div className="metric-card">
                  <div className="metric-label">P&L Return (Gross)</div>
                  <div className="metric-value numerals positive">{formatPercentage(monthlyReport.performance_summary.pnl_gross_return_percent)}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">P&L Return (Net)</div>
                  <div className="metric-value numerals positive">{formatPercentage(monthlyReport.performance_summary.pnl_net_return_percent)}</div>
                </div>
              </div>
            </section>

            <section className="section">
              <h2 className="section-title">Profit & Loss Statement</h2>
              <table className="data-table pnl-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Amount (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="total-row">
                    <td>Net P&L</td>
                    <td className="numerals positive highlight">{formatCurrency(monthlyReport.net_pnl_after_fees)}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;